import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Briefcase, Gem, Loader2, PartyPopper, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { fmt } from "../../data/site";
import { PhotoUploader } from "../PhotoUploader";

export const PACKAGE_ICONS = { briefcase: Briefcase, party: PartyPopper, gem: Gem };

const EMPTY_PACKAGE = { name: "", subtitle: "", price: 25, img: "", includes: [], icon: "party", active: true };

const inputCls = "w-full rounded-xl border border-inkline bg-parchment px-4 py-2.5 text-sm text-ink outline-none transition-colors placeholder:text-muted2/60 focus:border-spice";

const PackageCard = ({ pkg, onEdit, onDelete, onToggle }) => {
  const Icon = PACKAGE_ICONS[pkg.icon] || PartyPopper;
  const short = pkg.id.slice(0, 8);
  return (
    <div
      data-testid={`catering-item-${short}`}
      className={`overflow-hidden rounded-2xl border border-inkline bg-parchment transition-all ${pkg.active ? "" : "opacity-55"}`}
    >
      <div className="relative">
        <img src={imgSrc(pkg.img)} alt={pkg.name} className="h-36 w-full object-cover" />
        <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-parchment/90 text-spice">
          <Icon size={16} />
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="font-display text-base font-semibold leading-snug text-ink">{pkg.name}</h4>
            <p className="text-xs text-muted2">{pkg.subtitle}</p>
          </div>
          <span className="shrink-0 font-mono text-sm font-bold text-spice">{fmt(pkg.price)}/pp</span>
        </div>
        <p className="mt-2 text-xs text-ink/70">{pkg.includes.length} inclusions</p>
        <div className="mt-3 flex items-center gap-2">
          <button
            data-testid={`catering-active-${short}`}
            onClick={() => onToggle(pkg)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${pkg.active ? "bg-halal/10 text-halal" : "bg-ink/10 text-ink"}`}
          >
            {pkg.active ? "Live" : "Hidden"}
          </button>
          <button
            data-testid={`catering-edit-${short}`}
            onClick={() => onEdit(pkg)}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-bold text-parchment transition-colors hover:bg-spice"
          >
            <Pencil size={12} /> Edit
          </button>
          <button
            data-testid={`catering-delete-${short}`}
            onClick={() => onDelete(pkg)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-spice/40 px-3.5 py-1.5 text-xs font-bold text-spice transition-colors hover:bg-spice hover:text-parchment"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const PackageForm = ({ editing, setEditing, onClose, onSave, busy }) => {
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      data-testid="catering-form-modal"
    >
      <motion.form
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSave}
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-parchment p-7 sm:rounded-3xl sm:p-9"
        data-testid="catering-form"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold text-ink">{editing.id ? "Edit catering package" : "New catering package"}</h3>
          <button type="button" data-testid="catering-cancel-btn" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-cream text-ink transition-colors hover:bg-spice hover:text-parchment" aria-label="Close editor">
            <X size={17} />
          </button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <input required data-testid="catering-form-name" placeholder="Package name (e.g. The Gathering)" value={editing.name} onChange={set("name")} className={inputCls} />
          <input required type="number" step="0.5" min="1" data-testid="catering-form-price" placeholder="Price per person ($)" value={editing.price} onChange={set("price")} className={inputCls} />
        </div>
        <input data-testid="catering-form-subtitle" placeholder="Subtitle (e.g. Parties & grazing tables)" value={editing.subtitle} onChange={set("subtitle")} className={`mt-4 ${inputCls}`} />
        <div className="mt-4">
          <PhotoUploader value={editing.img} onChange={(img) => setEditing((cur) => ({ ...cur, img }))} folder="catering" label="Package photo" testid="catering-form-img" />
        </div>
        <div className="mt-4">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">Card icon</div>
          <div className="flex gap-2">
            {Object.entries(PACKAGE_ICONS).map(([key, Icon]) => (
              <button
                key={key} type="button" data-testid={`catering-icon-${key}`}
                onClick={() => setEditing({ ...editing, icon: key })}
                className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all ${editing.icon === key ? "bg-ink text-saffron" : "bg-cream text-ink/60 hover:bg-ink/10"}`}
                aria-label={`${key} icon`}
              >
                <Icon size={18} />
              </button>
            ))}
          </div>
        </div>
        <textarea
          data-testid="catering-form-includes" rows={5}
          placeholder={"What's included — one per line\nChoice of 3 signature entrees\nGrazing & mezze station"}
          value={editing.includes.join("\n")}
          onChange={(e) => setEditing({ ...editing, includes: e.target.value.split("\n") })}
          className={`mt-4 ${inputCls}`}
        />
        <label className="mt-4 flex items-center gap-3 text-sm font-medium text-ink">
          <input type="checkbox" data-testid="catering-form-active" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 accent-[#305830]" />
          Live on the Catering page
        </label>
        <button type="submit" data-testid="catering-save-btn" disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-spice py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-ink disabled:opacity-60">
          {busy && <Loader2 size={15} className="animate-spin" />}
          {editing.id ? "Save Changes" : "Add Package"}
        </button>
      </motion.form>
    </motion.div>
  );
};

export const CateringEditor = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/catering-packages");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { id, order, ...rest } = editing;
    const payload = { ...rest, price: Number(editing.price), includes: editing.includes.map((s) => s.trim()).filter(Boolean) };
    try {
      if (id) await api.patch(`/admin/catering-packages/${id}`, payload);
      else await api.post("/admin/catering-packages", payload);
      toast.success(id ? "Package updated." : "Package added — it's live on the Catering page.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pkg) => {
    try {
      await api.delete(`/admin/catering-packages/${pkg.id}`);
      toast.success(`"${pkg.name}" removed.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (pkg) => {
    try {
      const { id, order, ...rest } = pkg;
      await api.patch(`/admin/catering-packages/${id}`, { ...rest, active: !pkg.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="catering-editor">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted2">
          {items.filter((i) => i.active).length} live packages · changes appear on the Catering page instantly.
        </p>
        <button data-testid="catering-add-btn" onClick={() => setEditing({ ...EMPTY_PACKAGE })} className="inline-flex items-center gap-2 rounded-full bg-spice px-5 py-2.5 text-sm font-bold text-parchment transition-colors hover:bg-ink">
          <Plus size={15} /> Add Package
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((pkg) => (
          <PackageCard key={pkg.id} pkg={pkg} onEdit={setEditing} onDelete={remove} onToggle={toggleActive} />
        ))}
      </div>
      <AnimatePresence>
        {editing && <PackageForm editing={editing} setEditing={setEditing} onClose={() => setEditing(null)} onSave={save} busy={busy} />}
      </AnimatePresence>
    </div>
  );
};
